import { DEFAULT_SETTINGS, LEGACY_KEY, STORAGE_KEY } from '../core/constants';
import { compact } from '../core/history';
import { createKV, loadData, sanitize, serialize } from '../core/storage';
import type { AppData, Settings } from '../core/types';

export type Topic = 'run' | 'settings' | 'tasks' | 'stats' | 'side' | 'storage';

const kv = createKV(safeLocalStorage());

function safeLocalStorage(): Storage | null {
  // Even `typeof localStorage` runs the getter, which throws a SecurityError when site data is
  // blocked — so every access stays inside the try.
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/** First-visit defaults follow the OS colour scheme. */
export function baseSettings(): Settings {
  const dark = typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches;
  return { ...DEFAULT_SETTINGS, theme: dark ? 'dark' : 'light' };
}

let lastWritten = '';

export const app: { data: AppData; storageOk: boolean } = {
  data: loadData(null, null, Date.now()).data,
  storageOk: kv.persistent,
};

/** Loads persisted data. Returns where it came from (fresh / v2 / migrated legacy). */
export function loadState(now = Date.now()) {
  const raw = kv.get(STORAGE_KEY);
  const r = loadData(raw, kv.get(LEGACY_KEY), now, baseSettings());
  app.data = r.data;
  lastWritten = raw ?? ''; // baseline for cross-tab change detection
  if (r.source === 'legacy') persist();
  return r.source;
}

/** Replace all state (backup import / erase). */
export function replaceState(data: AppData) {
  app.data = data;
  persist();
  emit('run', 'settings', 'tasks', 'stats', 'side');
}

let warnedOnce = false;

export function persist() {
  compact(app.data.history, Date.now());
  const json = serialize(app.data);
  lastWritten = json;
  const ok = kv.set(STORAGE_KEY, json);
  if (!ok && !warnedOnce) {
    warnedOnce = true;
    app.storageOk = false;
    emit('storage');
  }
}

/**
 * Re-reads storage written by another tab. Returns true if state changed.
 * Used both for `storage` events and right before completing a phase (so two open tabs never
 * double-log the same session).
 */
export function syncFromStorage(json: string | null = kv.get(STORAGE_KEY)): boolean {
  if (!json || json === lastWritten) return false;
  try {
    app.data = sanitize(JSON.parse(json), Date.now(), app.data.settings);
    lastWritten = json;
    return true;
  } catch {
    return false;
  }
}

export function eraseAll() {
  kv.remove(STORAGE_KEY);
  kv.remove(LEGACY_KEY);
}

/* ---------- tiny typed event bus ---------- */
const listeners = new Map<Topic, Set<() => void>>();

export function on(topic: Topic, fn: () => void) {
  if (!listeners.has(topic)) listeners.set(topic, new Set());
  listeners.get(topic)!.add(fn);
}

/** Notify subscribers; each listener runs at most once per call even if subscribed to several topics. */
export function emit(...topics: Topic[]) {
  const seen = new Set<() => void>();
  for (const t of topics)
    for (const fn of listeners.get(t) ?? [])
      if (!seen.has(fn)) {
        seen.add(fn);
        fn();
      }
}

/** Mutate + persist + notify in one step. */
export function commit(topics: Topic[], mutate?: (d: AppData) => void) {
  mutate?.(app.data);
  persist();
  emit(...topics);
}
